import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';

import {
  getCustomerAccounts,
  getCustomerAddresses,
  getCustomerConsents,
  getCustomerContacts,
  getCustomerRecipients,
  getCustomerSummary,
  searchCustomers,
} from '../api/customersApi';
import type {
  CustomerAccountSummary,
  CustomerAddress,
  CustomerConsent,
  CustomerContact,
  CustomerRecipient,
  CustomerSearchRequest,
  CustomerSearchResponse,
  CustomerSummary,
} from '../model/types';
import { customersKeys } from './queryKeys';

interface EnabledOption {
  enabled?: boolean;
}

export function useCustomerSearch(
  request: CustomerSearchRequest,
  options?: EnabledOption,
): UseQueryResult<CustomerSearchResponse, NormalizedApiError> {
  return useQuery<CustomerSearchResponse, NormalizedApiError>({
    queryKey: customersKeys.search(request),
    queryFn: () => searchCustomers(request),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useCustomerSummary(
  customerId: string | null,
  options?: EnabledOption,
): UseQueryResult<CustomerSummary, NormalizedApiError> {
  return useQuery<CustomerSummary, NormalizedApiError>({
    queryKey: customersKeys.summary(customerId ?? ''),
    queryFn: () => getCustomerSummary(customerId as string),
    enabled: Boolean(customerId) && (options?.enabled ?? true),
  });
}

export function useCustomerContacts(
  customerId: string | null,
  options?: EnabledOption,
): UseQueryResult<CustomerContact[], NormalizedApiError> {
  return useQuery<CustomerContact[], NormalizedApiError>({
    queryKey: customersKeys.contacts(customerId ?? ''),
    queryFn: () => getCustomerContacts(customerId as string),
    enabled: Boolean(customerId) && (options?.enabled ?? true),
  });
}

export function useCustomerAddresses(
  customerId: string | null,
  options?: EnabledOption,
): UseQueryResult<CustomerAddress[], NormalizedApiError> {
  return useQuery<CustomerAddress[], NormalizedApiError>({
    queryKey: customersKeys.addresses(customerId ?? ''),
    queryFn: () => getCustomerAddresses(customerId as string),
    enabled: Boolean(customerId) && (options?.enabled ?? true),
  });
}

export function useCustomerRecipients(
  customerId: string | null,
  options?: EnabledOption,
): UseQueryResult<CustomerRecipient[], NormalizedApiError> {
  return useQuery<CustomerRecipient[], NormalizedApiError>({
    queryKey: customersKeys.recipients(customerId ?? ''),
    queryFn: () => getCustomerRecipients(customerId as string),
    enabled: Boolean(customerId) && (options?.enabled ?? true),
  });
}

export function useCustomerConsents(
  customerId: string | null,
  options?: EnabledOption,
): UseQueryResult<CustomerConsent[], NormalizedApiError> {
  return useQuery<CustomerConsent[], NormalizedApiError>({
    queryKey: customersKeys.consents(customerId ?? ''),
    queryFn: () => getCustomerConsents(customerId as string),
    enabled: Boolean(customerId) && (options?.enabled ?? true),
  });
}

export function useCustomerAccounts(
  customerId: string | null,
  options?: EnabledOption,
): UseQueryResult<CustomerAccountSummary[], NormalizedApiError> {
  return useQuery<CustomerAccountSummary[], NormalizedApiError>({
    queryKey: customersKeys.accounts(customerId ?? ''),
    queryFn: () => getCustomerAccounts(customerId as string),
    enabled: Boolean(customerId) && (options?.enabled ?? true),
  });
}
